import React, {useMemo} from "react";
import { PersonAndBoxMessage } from "../../../components/messages/PersonAndBoxMessage";
import { useSnackbar } from "../../../context/snackbar-context";
import { useSnackbarDisplayOnError } from "../../../hooks/error/useSnackbarDisplayOnError";
import { useBindSearchQuery } from "../../../hooks/useBindSearchQuery";
import { useGetBoundReportPaginated } from "../../../hooks/request/useGetBoundReportPaginated";
import { BoundTablePagination } from "../../../components/common/inputs/boundInputs";
import { BuyerInventoryReportVO } from "../../../types/model/valueObject/inventory/BuyerInventoryReportVO";
import { BuyerCurrentInventorySortFilter, BuyerCurrentInventorySearchParams } from "../../../components/buyer/currentInventoryReport/BuyerCurrentInventorySortFilter";
import { BuyerCurrentInventoryCardListWithLoader } from "../../../components/buyer/currentInventoryReport/BuyerCurrentInventoryCardList";
import { buyerInventoryReportEndpoints as reportEndpoints } from "../../../requests/api-endpoints";
import { useReportDataSummaryCollapseByIndex}  from "../../../hooks/report/useReportDataSummaryCollapseByIndex";
import { useBoundQueryDownloadUrl } from "../../../hooks/report/useBoundQueryDownloadUrl";
import DownloadCSVButton from "../../../components/report/export/DownloadCSVButton";
import {
  DownloadButtonsContainer,
  ReportDataSummary
} from "../../../components/report/redesigned";
import {
  SecurSpaceReportTitle
} from "../../../components/common";

// a default query MUST be defined outside of any components to avoid re-initialization on each render
const DEFAULT_QUERY: BuyerCurrentInventorySearchParams = {
  locationId: '',
  bookingNumber: '',
  equipmentNumber: '',
  equipmentType: '',
  sortBy: "CHECK_IN_DATE",
  sortDir: 'desc',
  page: 0,
  size: 10,
};

const BuyerCurrentInventoryReport = () => {
  const { snackbarShowMessage } = useSnackbar();
  // state hooks
  const [reportQuery, setReportQuery] = useBindSearchQuery(DEFAULT_QUERY);

  // Api hooks
  const {
    reportPayload,
    pageInfo,
    isLoading,
    error
  } =  useGetBoundReportPaginated<BuyerInventoryReportVO[]>({
    requestUrl: reportEndpoints.search,
    requestParams: reportQuery
  });

  const {
    collapseByIndex: expandByIndex,
    handleToggleCollapse: handleToggleExpand,
    hasExpandedItems,
    setExpandedItems,
    setExpandedAll,
    handleExpandAll,
  } = useReportDataSummaryCollapseByIndex({ data: reportPayload });

  // error hooks
  useSnackbarDisplayOnError({error, snackbarShowMessage });

  // conditional display hooks
  // Not gated on `isLoading`: the previous payload survives a refetch, so
  // leaving this on keeps the empty state put instead of blinking out and
  // back on every filter change. It is undefined until the first load, so it
  // cannot flash before there is an answer.
  const shouldShowPersonAndBox = useMemo(() => {
    return !error && reportPayload?.length === 0
  }, [error, reportPayload]);

  const showPagination = useMemo(() => {
    return !!(reportPayload?.length && reportPayload.length > 0);
  }, [reportPayload])

  // change handlers
  const handleFilterChange = (newQuery: BuyerCurrentInventorySearchParams) => {
    const updatedQuery = {
      ...reportQuery,
      ...newQuery
    };
    if (!newQuery.page) {
      // reset to first page on filter change if it is not being explicitly set
      updatedQuery.page = 0;
    }

    setReportQuery(updatedQuery);
  }

  const downloadUrl = useBoundQueryDownloadUrl({ endpoint: reportEndpoints.report, query: reportQuery });

  //NOTE: since this report is contained within the ReportOverview tabs, it should not be wrapped in a
  //      SecurSpaceContainer or SecurSpaceReportContainer, as those are already present in the parent
  //      component
  return (
    <>
      <SecurSpaceReportTitle>Current Inventory</SecurSpaceReportTitle>
      <BuyerCurrentInventorySortFilter
        query={reportQuery}
        onChange={handleFilterChange}
      />
      <ReportDataSummary
        visible={!shouldShowPersonAndBox}
        count={pageInfo?.count || 0}
        title={"All"}
        isShowCollapseButton={true}
        hasExpandedItems={hasExpandedItems}
        setExpandedItems={setExpandedItems}
        setExpandedAll={setExpandedAll}
        handleExpandAll={handleExpandAll}
        downloadButtonsSlot={
          <DownloadButtonsContainer>
            <DownloadCSVButton title='Download Report' reportUrl={downloadUrl} />
          </DownloadButtonsContainer>
        }
      />
      <BuyerCurrentInventoryCardListWithLoader
        visible={!error}
        isLoading={isLoading}
        items={reportPayload}
        expandByIndex={expandByIndex}
        onToggleExpand={handleToggleExpand}
      />
      <PersonAndBoxMessage
        visible={shouldShowPersonAndBox }
      />
      <BoundTablePagination<BuyerCurrentInventorySearchParams>
        visible={showPagination}
        pageInfo={pageInfo}
        onChange={handleFilterChange}
      />
    </>
  );
};

export default BuyerCurrentInventoryReport;
